import "@/App.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import useIsMobile from "@/hooks/useIsMobile";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import PublicLayout from "@/components/PublicLayout";
import MobileShell from "@/components/mobile/MobileShell";

import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import ShopDetail from "@/pages/ShopDetail";
import Auth from "@/pages/Auth";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import CustomerDashboard from "@/pages/CustomerDashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AdminPanel from "@/pages/AdminPanel";
import AppAdmin from "@/pages/AppAdmin";
import MobileHome from "@/pages/mobile/MobileHome";
import MobileMap from "@/pages/mobile/MobileMap";
import MobileOffers from "@/pages/mobile/MobileOffers";
import MobileFavorites from "@/pages/mobile/MobileFavorites";
import MobileCoupons from "@/pages/mobile/MobileCoupons";
import MobileMessages from "@/pages/mobile/MobileMessages";
import MobileProfile from "@/pages/mobile/MobileProfile";
import MobileNotifications from "@/pages/mobile/MobileNotifications";

function MobileRedirect({ children }) {
  const isMobile = useIsMobile();
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return children;
  const shouldRedirect =
    isMobile && user && user.role === "customer" && (pathname === "/" || pathname === "/browse");
  if (shouldRedirect) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth callback synchronously before any route/guard runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <MobileRedirect>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/shops/:id" element={<ShopDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
        </Route>

        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/app-admin" element={<AppAdmin />} />

        {/* Mobile app shell */}
        <Route path="/app" element={<MobileShell />}>
          <Route index element={<MobileHome />} />
          <Route path="map" element={<MobileMap />} />
          <Route path="offers" element={<MobileOffers />} />
          <Route path="favorites" element={<MobileFavorites />} />
          <Route path="coupons" element={<MobileCoupons />} />
          <Route path="messages" element={<MobileMessages />} />
          <Route path="profile" element={<MobileProfile />} />
          <Route path="notifications" element={<MobileNotifications />} />
        </Route>

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute roles={["customer", "shop_owner", "admin"]}>
              <CustomerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner"
          element={
            <ProtectedRoute roles={["shop_owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
      </Routes>
    </MobileRedirect>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
